import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Button } from '@dsny/dsny-component-library'
import InfiniteScroll from 'external/infiniteScroll/app'
import { searchLogo } from 'images'
import {
  PlaylistSort,
  getOptionDisplays,
  getValueFromDisplay,
  getDisplayFromValue
} from 'constants/enums'
import ModalActions from 'reducers/Modal'
import PlaylistActions, { thunks as PlaylistThunks } from 'reducers/PlayList'
import { MainLayout } from 'containers'
import {
  PlaylistCell,
  PlayPlaylistModal,
  InputSelectDropdown,
  CollectionContent
} from 'components'
import { RESPONSIVE_BREAKPOINT_PAGES } from 'constants/responsiveBreakpoints'
import PlaylistCard from 'components/playlist/PlaylistCard'
import RemovePlaylistModal from 'components/playlist/RemovePlaylistModal'
import {
  RESPONSIVE_BREAKPOINT_VERSIONS,
  ResponsiveBreakpointsProvider,
  useResponsiveBreakpointsVersion
} from 'contexts/ResponsiveBreakpointsContext'
import { useDesktopMediaQuery } from 'utils/hooks/useMediaQueries'
import usePlayerPlaylists from 'utils/hooks/usePlayerPlaylists'
import usePlaylistPlayback from 'utils/hooks/usePlaylistPlayback'
import usePlaylistRemoval from 'utils/hooks/usePlaylistRemoval'
import {
  Row,
  CreateButton,
  Icon,
  Label,
  PlaylistGrid,
  LibraryGrid,
  LibraryStatus
} from './Playlist.styles'
import { getPlaylistData } from './selectors'
import PlaylistWithRollout from './PlaylistWithRollout'
import FirstPlaylistWelcome from './FirstPlaylistWelcome'
import PlaylistLibraryToolbar from './PlaylistLibraryToolbar'

export const getLegacyItemsPerRow = (windowWidth) =>
  Math.max(1, Math.min(Math.floor(windowWidth / 400), 7))

export const PlaylistContent = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const scrollView = useRef(null)
  const [itemsPerRow, setItemsPerRow] = useState(2)
  const [selectedPlaylist, setSelectedPlaylist] = useState(null)
  const responsiveBreakpointsVersion = useResponsiveBreakpointsVersion()
  const isDesktopV2 = useDesktopMediaQuery()
  const {
    enabled: isPlayerPlaylistsEnabled,
    isLoading: isPlayerPlaylistsLoading
  } = usePlayerPlaylists()
  const {
    analyticsModalType,
    cancelPlaylistPlayback,
    confirmPlaylistPlayback,
    hasPlaybackError,
    isConfirmationOpen,
    isPlaybackPending,
    pendingPlaylist,
    requestPlaylistPlayback
  } = usePlaylistPlayback()
  const {
    cancelPlaylistRemoval,
    confirmPlaylistRemoval,
    hasRemovalError,
    isRemovalOpen,
    isRemovalPending,
    pendingRemovalPlaylist,
    requestPlaylistRemoval
  } = usePlaylistRemoval()
  const responsiveBreakpointsV2 =
    responsiveBreakpointsVersion === RESPONSIVE_BREAKPOINT_VERSIONS.V2

  const {
    playlists,
    page,
    isIpadView,
    loading,
    error,
    requestedPage = 1,
    listRevision = 0,
    totalItemsCount,
    sort,
    searchToken
  } = useSelector(getPlaylistData)

  const isRedesignedLibrary =
    isPlayerPlaylistsEnabled && !isPlayerPlaylistsLoading
  const hasMore =
    !error && (totalItemsCount === null || totalItemsCount > playlists.length)

  const updateData = useCallback(
    (nextPage) => {
      dispatch(PlaylistThunks.list({ page: nextPage, searchToken, sort }))
    },
    [searchToken, sort]
  )

  const calculateItemsPerRow = useCallback(() => {
    setItemsPerRow(getLegacyItemsPerRow(window.innerWidth))
  }, [])

  useEffect(() => {
    updateData(1)
  }, [])

  useEffect(() => {
    if (isRedesignedLibrary) {
      const content = document.getElementById('playlist-library-scroll')
      if (content) content.scrollTop = 0
    }
  }, [isRedesignedLibrary, listRevision])

  useEffect(() => {
    if (responsiveBreakpointsV2 || isRedesignedLibrary) {
      return undefined
    }

    calculateItemsPerRow()
    window.addEventListener('resize', calculateItemsPerRow)
    return () => window.removeEventListener('resize', calculateItemsPerRow)
  }, [calculateItemsPerRow, responsiveBreakpointsV2, isRedesignedLibrary])

  // Redux remains the rollback source until the App shell phase retires it.
  const showCompactInteractions = responsiveBreakpointsV2
    ? !isDesktopV2
    : isIpadView

  const onLoadMore = useCallback(() => {
    if (!loading && hasMore) {
      dispatch(PlaylistThunks.list({ page: page + 1 }))
    }
  }, [page, loading, hasMore])

  function handleCreateButton() {
    dispatch(ModalActions.openPlaylistModal('playlist_library'))
  }

  function handleSearchChange(event) {
    const nextSearchToken = event.target.value
    dispatch(PlaylistActions.updateSearchToken(nextSearchToken))
    if (nextSearchToken.length === 0) {
      updateData(1)
    }
  }

  function handlePlayClick(playlist) {
    requestPlaylistPlayback(playlist, 'playlist_library_card')
  }

  function handleRemoveClick(playlist) {
    requestPlaylistRemoval(playlist, 'playlist_library_card')
  }

  function toggleStarClick(playlist) {
    if (playlist.pinned) {
      dispatch(PlaylistThunks.unpin(playlist))
    } else {
      dispatch(PlaylistThunks.pin(playlist))
    }
  }

  function handleSortChange(value) {
    dispatch(PlaylistActions.updateSort(value))
    updateData(1)
  }

  function handleSearchSubmit() {
    updateData(1)
  }

  function handleSearchKeyDown(event) {
    if (event.key === 'Enter') handleSearchSubmit()
  }

  function handleRetry() {
    updateData(requestedPage)
  }

  function handleLegacySortChange(display) {
    handleSortChange(getValueFromDisplay(PlaylistSort, display))
  }

  const renderSearchBar = (isCompactView) => {
    return (
      <div className="flex flex-row items-center">
        <div className="flex flex-row items-center h-8 rounded-full bg-white font-header transition mr-4 w-72 px-4">
          <input
            value={searchToken}
            type="search"
            placeholder={t('home.search')}
            className="text-sm-placeholder focus:outline-none w-full"
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown} />
        </div>
        {!isCompactView && (
          <button
            type="button"
            className={`${
              searchToken === '' ? 'opacity-50 cursor-default' : 'opacity-100'
            } slowTransition focus:outline-none`}
            onClick={handleSearchSubmit}>
            <img src={searchLogo} className="w-8" alt="search button" />
          </button>
        )}
      </div>
    )
  }

  const renderSubHeader = () => {
    return (
      <>
        <Row className="mb-12">
          <Row>
            {renderSearchBar(showCompactInteractions)}
            <InputSelectDropdown
              value={sort ? getDisplayFromValue(PlaylistSort, sort) : ''}
              options={getOptionDisplays(PlaylistSort)}
              className="rounded-sm w-32 ml-4"
              t={t}
              onChange={handleLegacySortChange} />
          </Row>
          <CreateButton className="pr-10" onClick={handleCreateButton}>
            <Icon className="icon">+</Icon>
            <Label className="label">{t('playlist.create-new')}</Label>
          </CreateButton>
        </Row>
      </>
    )
  }

  const renderTable = () => {
    return (
      <InfiniteScroll
        ref={scrollView}
        dataLength={playlists.length}
        hasMore={hasMore}
        next={onLoadMore}
        loader={<h4>Loading...</h4>}
        endMessage={<p style={{ textAlign: 'center' }}>You have seen it all</p>}>
        <PlaylistGrid $responsiveBreakpointsV2={responsiveBreakpointsV2}>
          {playlists.map((item) => (
            <div
              key={item.playlistid}
              className={responsiveBreakpointsV2 ? undefined : 'inline-block'}
              style={
                responsiveBreakpointsV2
                  ? undefined
                  : { width: `${100 / itemsPerRow}%` }
              }>
              <PlaylistCell
                playlist={item}
                isIpadView={showCompactInteractions}
                selectedPlaylist={selectedPlaylist}
                toggleStarClick={toggleStarClick}
                setSelectedPlaylist={setSelectedPlaylist}
                onPlayClick={handlePlayClick} />
            </div>
          ))}
        </PlaylistGrid>
      </InfiniteScroll>
    )
  }

  const renderLibrary = () => (
    <>
      <InfiniteScroll
        dataLength={playlists.length}
        hasMore={hasMore && !loading}
        loader={<span aria-hidden="true" />}
        resetKey={listRevision}
        scrollableTarget="playlist-library-scroll"
        next={onLoadMore}>
        <LibraryGrid>
          {!(error && requestedPage === 1) && playlists.map((playlist) => (
            <PlaylistCard
              key={playlist.playlistid}
              isPlaybackPending={isPlaybackPending}
              isRemovalPending={isRemovalPending}
              playlist={playlist}
              onPlayClick={handlePlayClick}
              onRemoveClick={handleRemoveClick} />
          ))}
        </LibraryGrid>
      </InfiniteScroll>
      {loading && (
        <LibraryStatus aria-live="polite" role="status">
          {t('general.loading')}
        </LibraryStatus>
      )}
      {!loading && error && (
        <LibraryStatus role="alert">
          <p>{t('playlist.load-error')}</p>
          <Button
            setting="player"
            type="button"
            variant="secondary"
            onClick={handleRetry}>
            {t('playlist.retry')}
          </Button>
        </LibraryStatus>
      )}
      {!loading && !error && playlists.length === 0 && searchToken !== '' && (
        <LibraryStatus role="status">
          {t('playlist.no-search-results')}
        </LibraryStatus>
      )}
    </>
  )

  const showFirstRun =
    isRedesignedLibrary &&
    !loading &&
    !error &&
    totalItemsCount === 0 &&
    searchToken === ''

  const library = isRedesignedLibrary ? renderLibrary() : renderTable()
  const toolbar = isRedesignedLibrary ? (
    <PlaylistLibraryToolbar
      searchToken={searchToken}
      sort={sort}
      onCreate={handleCreateButton}
      onSearchChange={handleSearchChange}
      onSearchSubmit={handleSearchSubmit}
      onSortChange={handleSortChange} />
  ) : (
    renderSubHeader()
  )
  const table = showFirstRun ? <FirstPlaylistWelcome /> : library
  const subHeader = showFirstRun ? undefined : toolbar

  return (
    <div
      className="contents"
      data-responsive-breakpoints={responsiveBreakpointsVersion}
      data-responsive-breakpoints-page={RESPONSIVE_BREAKPOINT_PAGES.PLAYLIST}>
      <CollectionContent
        contentId={isRedesignedLibrary ? 'playlist-library-scroll' : undefined}
        title={t('collections.playlists')}
        table={table}
        hasGradient={!isRedesignedLibrary}
        isPlaylistLibrary={isRedesignedLibrary && !showFirstRun}
        isWelcome={showFirstRun}
        subHeader={subHeader} />
      <PlayPlaylistModal
        analyticsModalType={analyticsModalType}
        hasError={hasPlaybackError}
        isLoading={isPlaybackPending}
        isOpen={isConfirmationOpen}
        playlist={pendingPlaylist}
        onCancel={cancelPlaylistPlayback}
        onConfirm={confirmPlaylistPlayback} />
      <RemovePlaylistModal
        hasError={hasRemovalError}
        isLoading={isRemovalPending}
        isOpen={isRemovalOpen}
        playlist={pendingRemovalPlaylist}
        onCancel={cancelPlaylistRemoval}
        onConfirm={confirmPlaylistRemoval} />
    </div>
  )
}

const Playlist = () => (
  <ResponsiveBreakpointsProvider value={RESPONSIVE_BREAKPOINT_VERSIONS.LEGACY}>
    <MainLayout
      content={
        <PlaylistWithRollout>
          <PlaylistContent />
        </PlaylistWithRollout>
      } />
  </ResponsiveBreakpointsProvider>
)

export default Playlist
